import { useEffect, useRef, useState } from "react";
import { Button } from "@/deps/ui-library";
import { api } from "@/api";
import type { DocumentPdfKind, Id } from "@/api/types";
import { useDocumentPdfEnabled } from "@/app/session";

/** How often to ask, and how long before saying it's taking too long. */
const POLL_MS = 2000;
const GIVE_UP_MS = 90_000;

/**
 * Asks for an issued document as a PDF and starts the download when it's ready. Rendering happens in
 * another service, so it takes a few seconds. Hidden until the deployment has it set up.
 */
export function DownloadPdfButton({
  kind,
  id,
  number,
}: {
  kind: DocumentPdfKind;
  id: Id;
  number: string | null;
}) {
  const enabled = useDocumentPdfEnabled();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  if (!enabled) return null;

  const start = async () => {
    setBusy(true);
    setProblem(null);
    const stop = (message: string | null) => {
      setBusy(false);
      setProblem(message);
    };

    try {
      const jobId = await api.documents.requestPdf(kind, id);
      const startedAt = Date.now();

      const check = async () => {
        try {
          const status = await api.documents.pdfStatus(jobId, number ?? "document");
          if (status.state === "ready" && status.url) {
            stop(null);
            window.location.assign(status.url);
          } else if (status.state === "failed") {
            stop("That PDF could not be made. Print the document instead.");
          } else if (Date.now() - startedAt > GIVE_UP_MS) {
            stop("That PDF is taking too long. Try again, or print the document instead.");
          } else {
            timer.current = setTimeout(() => void check(), POLL_MS);
          }
        } catch (error) {
          stop(error instanceof Error ? error.message : "That PDF could not be made.");
        }
      };
      await check();
    } catch (error) {
      stop(error instanceof Error ? error.message : "That PDF could not be made.");
    }
  };

  return (
    <>
      {problem ? (
        <span role="alert" className="text-bad-text text-xs">
          {problem}
        </span>
      ) : null}
      <Button
        colorScheme="default"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => void start()}
      >
        {busy ? "Preparing PDF" : "Download PDF"}
      </Button>
    </>
  );
}
